/* ===========================================================
   editor/nodes/code.js — code blocks (runnable in Developer mode)

   The normal code block, with a header bar. In Casual mode the header
   is just the language and a Copy button; in Developer mode Python,
   JavaScript and SQL blocks also get Run / Stop / Clear, and output
   appears underneath. Output is never saved into the doc: it belongs
   to whoever pressed Run.

   Code runs in the browser only, inside a sandboxed frame with its
   own throwaway origin (see public/sandbox/runner.html), never on the
   server, and never where it could reach the app's cookies or API.
   =========================================================== */

import { CodeBlock } from '@tiptap/extension-code-block';
import { env } from '../env.js';
import { h, svgIcon } from '../../lib/dom.js';
import { ICONS } from '../../lib/icons.js';

export const LANGS = [
  { id: 'python', label: 'Python', run: true },
  { id: 'javascript', label: 'JavaScript', run: true },
  { id: 'sql', label: 'SQL', run: true },
  { id: 'html', label: 'HTML' },
  { id: 'css', label: 'CSS' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'json', label: 'JSON' },
  { id: 'bash', label: 'Shell' },
  { id: 'markdown', label: 'Markdown' },
  { id: '', label: 'Plain text' },
];
const ALIASES = { js: 'javascript', py: 'python', ts: 'typescript', sh: 'bash', shell: 'bash', md: 'markdown', sqlite: 'sql', text: '', plaintext: '' };
export const normLang = l => { const v = String(l || '').toLowerCase(); return ALIASES[v] ?? v; };
const langInfo = l => LANGS.find(x => x.id === normLang(l)) || { id: normLang(l), label: l || 'Plain text' };

function button(icon, label, onclick, cls = '') {
  return h(`button.cb-btn${cls ? `.${cls}` : ''}`, { type: 'button', onclick, title: label, 'aria-label': label }, svgIcon(ICONS[icon], 15), h('span', {}, label));
}

function codeView({ node, editor, getPos }) {
  let current = node;
  const dev = env.mode() === 'dev';
  const dom = h('div.code-block');
  const head = h('div.cb-head', { contentEditable: 'false' });
  const code = h('code');
  const pre = h('pre', { spellcheck: 'false' }, code);
  const out = h('div.cb-out', { contentEditable: 'false', role: 'log', 'aria-live': 'polite', hidden: true });
  dom.append(head, pre, out);

  let job = null;
  const setLang = l => {
    const pos = getPos();
    if (typeof pos !== 'number') return;
    editor.chain().command(({ tr }) => { tr.setNodeMarkup(pos, undefined, { ...current.attrs, language: l || null }); return true; }).run();
  };

  const printLine = (stream, text) => {
    out.hidden = false;
    if (stream === 'table') { out.append(tableOf(text)); return; }
    if (stream === 'status') {
      const s = out.querySelector('.cb-status') || out.appendChild(h('div.cb-status'));
      s.textContent = text;
      return;
    }
    out.querySelector('.cb-status')?.remove();
    const last = out.lastElementChild;
    if (last && last.dataset.stream === stream && stream !== 'result') { last.textContent += text; }
    else out.append(h(`pre.cb-line.s-${stream}`, { 'data-stream': stream }, text));
    out.scrollTop = out.scrollHeight;
  };

  const runBtn = button('play', 'Run', () => run(), 'run');
  const stopBtn = button('stop', 'Stop', () => job?.stop(), 'stop');
  const clearBtn = button('eraser', 'Clear', () => { out.replaceChildren(); out.hidden = true; });
  const copyBtn = button('copy', 'Copy', async () => {
    try { await navigator.clipboard.writeText(current.textContent); env.notify('Copied.'); } catch { env.notify('Your browser didn\'t let me copy. Select the code and press Ctrl C.'); }
  });
  stopBtn.hidden = true;

  async function run() {
    if (job) return;
    out.replaceChildren();
    out.hidden = false;
    runBtn.hidden = true;
    stopBtn.hidden = false;
    printLine('status', 'Starting…');
    try {
      const r = await env.runner();
      job = r.run({
        lang: normLang(current.attrs.language),
        code: current.textContent,
        timeout: env.timeout(),
        onOut: printLine,
        onDone: (ok, ms, why) => {
          job = null;
          runBtn.hidden = false;
          stopBtn.hidden = true;
          out.querySelector('.cb-status')?.remove();
          out.append(h(`div.cb-done${ok ? '' : '.bad'}`, {}, why || (ok ? `Done in ${ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`}` : 'Stopped with an error')));
        },
      });
    } catch (err) {
      job = null;
      runBtn.hidden = false;
      stopBtn.hidden = true;
      printLine('err', `Couldn't start the code runner: ${err.message || err}`);
    }
  }

  function paintHead() {
    const info = langInfo(current.attrs.language);
    head.replaceChildren();
    if (dev && editor.isEditable) {
      const sel = h('select.cb-lang', { 'aria-label': 'Code language', onchange: e => setLang(e.target.value) },
        ...LANGS.map(l => h('option', { value: l.id, selected: l.id === info.id }, l.label)),
        LANGS.some(l => l.id === info.id) ? null : h('option', { value: info.id, selected: true }, info.label));
      head.append(sel);
    } else {
      head.append(h('span.cb-label', {}, info.label));
    }
    head.append(h('span.cb-space'));
    if (dev && info.run) head.append(runBtn, stopBtn, clearBtn);
    head.append(copyBtn);
    dom.dataset.lang = info.id;
  }
  paintHead();

  return {
    dom,
    contentDOM: code,
    update(n) {
      if (n.type !== current.type) return false;
      const langChanged = n.attrs.language !== current.attrs.language;
      current = n;
      if (langChanged) paintHead();
      return true;
    },
    stopEvent: e => head.contains(e.target) || out.contains(e.target),
    ignoreMutation: m => m.type !== 'selection' && !code.contains(m.target),
    destroy() { job?.stop(); },
  };
}

function tableOf({ columns = [], rows = [], note }) {
  const t = h('table.cb-table',
    {},
    h('thead', {}, h('tr', {}, ...columns.map(c => h('th', {}, String(c))))),
    h('tbody', {}, ...rows.slice(0, 500).map(r => h('tr', {}, ...r.map(v => h('td', {}, v === null ? 'NULL' : String(v)))))));
  const wrap = h('div.cb-table-wrap', {}, t);
  if (rows.length > 500) wrap.append(h('div.cb-note', {}, `Showing 500 of ${rows.length} rows.`));
  if (note) wrap.append(h('div.cb-note', {}, note));
  return wrap;
}

export const RunnableCode = CodeBlock.extend({
  addOptions() {
    return { ...this.parent?.(), enableTabIndentation: true, tabSize: 2, defaultLanguage: null };
  },
  addNodeView() { return props => codeView(props); },
});
